import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import styles from './styles.module.css';
import RMSvgIcon from '../../customComponents/CommonComponents/RMSvgIcon';

export type MenuItem = {
  id?: number;
  label: string;
  subLabel?: string;
  icon?: React.ReactNode;
  isDisabled?: boolean;
  onClick?: () => void;
  submenuItems?: MenuItem[];
  customSubmenuComponent?: React.ReactNode;
  shouldShowSelectedItemCheckmark?: boolean;
  isSelected?: boolean;
};

interface Props {
  title?: string;
  icon: React.ReactNode;
  dropDownItems?: MenuItem[];
  isDisabled?: boolean;
  onClick?: (itemId: any) => void;
  direction?: 'ltr' | 'rtl';
  isAlwaysOpen?: boolean;
}

const RMDropdown = (props: Props) => {
  const {
    icon,
    dropDownItems,
    isDisabled,
    direction = 'ltr',
    onClick,
    isAlwaysOpen = false,
  } = props;

  let rootProps = {};
  if (isAlwaysOpen) {
    rootProps = { open: true, onOpenChange: onClick };
  }

  return (
    <>
      {dropDownItems && (
        <DropdownMenu.Root dir={direction} modal={false} {...rootProps}>
          <DropdownMenu.Trigger id='rmdropdown-trigger' asChild disabled={isDisabled}>
            <button id='rmdropdown-trigger-button' onClick={onClick} style={isDisabled ? { opacity: '50%' } : {}}>
              {icon}
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content id='rmdropdown-content' className={styles.DropdownMenuContent} sideOffset={5}>
              {dropDownItems?.map((item, index) => (
                <React.Fragment key={item.id || index}>
                  <MenuItem item={item} />
                </React.Fragment>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}
    </>
  );
};

const MenuItem = ({ item }: { item: MenuItem }) => {
  const customMenuStyle = item.customSubmenuComponent ? { overflow: 'visible' } : {};
  if (
    item.customSubmenuComponent ||
    (item.submenuItems && item.submenuItems.length > 0)
  ) {
    return (
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger id='rmdropdown-menuitem-subtrigger' className={styles.DropdownMenuSubTrigger}>
          {item.icon && <span id='rmdropdown-menuitem-item-icon' className="mr-2">{item.icon}</span>}
          {item.label}
          <div id='rmdropdown-menuitem-actions' className={styles.RightSlot}>
            <RMSvgIcon id='rmdropdown-menuitem-arrowright' name="ArrowRight" width="20px" height="20px" fill="black" />
          </div>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.Portal>
          <DropdownMenu.SubContent
            id='rmdropdown-menuitem-subcontent'
            className={styles.DropdownMenuSubContent}
            style={customMenuStyle}
            sideOffset={2}
            alignOffset={-5}
          >
            {item.customSubmenuComponent ? (
              <div style={{ minWidth: '300px', width: 'fit-content' }}>
                {item.customSubmenuComponent}
              </div>
            ) : (
              item.submenuItems?.map((subItem, index) => (
                <React.Fragment key={subItem.id || index}>
                  <MenuItem item={subItem} />
                </React.Fragment>
              ))
            )}
          </DropdownMenu.SubContent>
        </DropdownMenu.Portal>
      </DropdownMenu.Sub>
    );
  }
  return (
    <DropdownMenu.Item
      id={`rmdropdown-menuitem-${item.id || item.label?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '')}`}
      className={styles.DropdownMenuItem}
      disabled={item.isDisabled}
      onClick={item.isDisabled ? null : item.onClick}
    >
      <div id='rmdropdown-menuitem-button'
        className={`flex items-center justify-between w-full ${
          item.isDisabled ? 'opacity-50' : ''
        }`}
      >
        <div id='rmdropdown-menuitem-icon-wrapper' className="flex items-center gap-2">
          {item.shouldShowSelectedItemCheckmark && item.isSelected && (
            <RMSvgIcon id='rmdropdown-menuitem-checkmark-icon' name="checkMark" width="20px" height="20px" fill="blue" />
          )}
          {item.icon && <span>{item.icon}</span>}
          <span id={`rmdropdown-menuitem-${item.label}`}>{item.label}</span>
        </div>

        {item.subLabel && <span id={`rmdropdown-menuitem-${item.subLabel}`} className="ml-auto text-gray-500">{item.subLabel}</span>}
      </div>
    </DropdownMenu.Item>
  );
};

export default RMDropdown;
